import { Link } from 'react-router-dom';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-bg-primary py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/login">
            <img
              src="/my-family-calendar-primary-icon.png?v=2"
              alt="My Family Calendar"
              className="w-16 h-16 mx-auto mb-4 rounded-xl"
            />
          </Link>
          <h1 className="text-3xl font-semibold text-text-primary">Terms of Service</h1>
          <p className="text-text-secondary mt-2">Last updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="card prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">1. Acceptance of Terms</h2>
            <p className="text-text-secondary">
              By accessing or using My Family Calendar ("the app", "we", "our"), you agree to be
              bound by these Terms of Service. If you do not agree to these terms, please do not
              use the app.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">2. Description of Service</h2>
            <p className="text-text-secondary">
              My Family Calendar is a family scheduling application that aggregates calendar events
              from Google Calendar and Microsoft Outlook, syncs with Google Tasks, and provides
              household management features including chore tracking and shared lists.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">3. Account and Access</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>You must sign in using a valid Google or Microsoft account</li>
              <li>You are responsible for maintaining the security of your account credentials</li>
              <li>You must be at least 13 years old to use this service</li>
              <li>One family group per installation; additional family members can be invited by the administrator</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">4. Acceptable Use</h2>
            <p className="text-text-secondary mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Use the app for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to any part of the service</li>
              <li>Interfere with or disrupt the service or servers</li>
              <li>Share your account access with unauthorized users</li>
              <li>Use the app to store or transmit malicious code</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">5. Third-Party Services</h2>
            <p className="text-text-secondary">
              The app integrates with third-party services including Google Calendar, Google Tasks,
              Microsoft Outlook, and Google Maps. Your use of these services is subject to their
              respective terms of service and privacy policies. We are not responsible for the
              availability or functionality of these third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">6. Data and Privacy</h2>
            <p className="text-text-secondary">
              Your use of the app is also governed by our{' '}
              <Link to="/privacy" className="text-blue hover:underline">
                Privacy Policy
              </Link>
              , which describes how we collect, use, and protect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">7. Service Availability</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>We strive to maintain high availability but do not guarantee uninterrupted service</li>
              <li>We may perform maintenance that temporarily affects availability</li>
              <li>Features may be added, modified, or removed at our discretion</li>
              <li>We reserve the right to terminate accounts that violate these terms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">8. Intellectual Property</h2>
            <p className="text-text-secondary">
              The app and its original content, features, and functionality are owned by us and
              are protected by international copyright, trademark, and other intellectual property laws.
              Your calendar data and personal information remain your property.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-text-secondary">
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
              SECURE, OR ERROR-FREE. YOUR USE OF THE APP IS AT YOUR OWN RISK.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">10. Limitation of Liability</h2>
            <p className="text-text-secondary">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED
              TO LOSS OF DATA, LOSS OF PROFITS, OR BUSINESS INTERRUPTION, ARISING FROM YOUR USE
              OF THE APP.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">11. Account Termination</h2>
            <p className="text-text-secondary">
              You may terminate your account at any time by disconnecting your calendar sources
              and requesting account deletion. We reserve the right to suspend or terminate accounts
              that violate these terms without prior notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-text-primary mb-4">12. Changes to Terms</h2>
            <p className="text-text-secondary">
              We reserve the right to modify these terms at any time. We will notify users of
              significant changes by posting the updated terms on this page. Your continued use
              of the app after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-4">13. Contact</h2>
            <p className="text-text-secondary">
              If you have any questions about these Terms of Service, please contact us through
              the app settings or visit our website.
            </p>
          </section>
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <Link to="/login" className="text-blue hover:underline">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
